import { Field, Label } from '@headlessui/react';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { CloseIcon } from '../../../assets/icons/index';
import MinusIcon from '../../../assets/icons/minus-icon';
import { QueryKey } from '../../../constants/index';
import { SharePlan, useFormat, useInvalidateQueries, useModalState } from '../../../hooks/index';
import { ApproveMilestoneDto } from '../../../types/vesting-tasks.types';
import { calcMilestoneIssuedShares } from '../../../utils/calc-milestone-issued-shares.util';
import { toNumber } from '../../../utils/to-number.util';
import { AlertDialogWrapper } from '../../alert-dialog/index';
import { AppFormattedMessage } from '../../app-formatted-message/index';
import Button from '../../button/index';
import { Checkbox } from '../../checkbox/index';
import { Table } from '../../table/index';
import { TargetBasedTableRow } from './target-based-table-row';
import {
  ApproveHurdleColumn,
  approveTargetBasedColumnMap,
  approveTargetBasedColumnTitle,
} from './types';

export type ApproveTargetBasedPlanProps = {
  companyId: string;
  milestoneIndex: null | number;
};

export const ApproveTargetBasedPlan: FC<ApproveTargetBasedPlanProps> = ({
  companyId,
  milestoneIndex,
}) => {
  const {
    isOpen,
    toggler,
    handleCloseModal,
    paramsValue: sharePlanId,
  } = useModalState('target-based-approve-modal');
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_UNREAD_NOTIFICATION_COUNT,
    QueryKey.APPROVE_TARGET_BASED_MILESTONE,
  );
  const [selectedVestingTasks, setSelectedVestingTasks] = useState<ApproveMilestoneDto[]>([]);

  const { sharePlan } = SharePlan.useSharePlan({ companyId, planId: sharePlanId });
  const { format } = useFormat();

  const { approveTargetBasedSharePlans } = SharePlan.useTargetBasedVestingTasksByPlan({
    companyId,
    sharePlanId: sharePlanId,
  });

  const resolvedMilestoneIndex = typeof milestoneIndex === 'number' ? milestoneIndex : null;

  const pendingTargetBasedSharePlans =
    resolvedMilestoneIndex === null
      ? []
      : approveTargetBasedSharePlans.filter(
          ({ finished, targetBasedVestingMilestones }) =>
            !finished && !targetBasedVestingMilestones?.[resolvedMilestoneIndex]?.isCompleted,
        );

  const referenceMilestones =
    pendingTargetBasedSharePlans[0]?.targetBasedVestingMilestones ||
    approveTargetBasedSharePlans[0]?.targetBasedVestingMilestones;

  const milestone =
    resolvedMilestoneIndex === null ? undefined : referenceMilestones?.[resolvedMilestoneIndex];

  const onDeselectAll = () => setSelectedVestingTasks([]);

  const onSelectAll = (data: ApproveMilestoneDto[]) => {
    setSelectedVestingTasks(data);
  };

  const { approve, isPending } = SharePlan.useApproveTargetBasedVestingTasks({
    companyId,
  });

  const vestingCompletedWeight = toNumber(
    referenceMilestones?.reduce(
      (acc, { isCompleted, weight }) => acc + toNumber(isCompleted && weight),
      0,
    ),
  );

  if (resolvedMilestoneIndex === null) return <></>;

  return (
    <AlertDialogWrapper control={{ onOpenChange: toggler, open: isOpen }}>
      <div className="flex flex-col gap-8 rounded-2xl bg-white p-8">
        <div className="flex w-full justify-between">
          <span className="text-xl font-semibold text-gray-700">Target Based Vesting Approval</span>
          <Button
            className="centered flex size-7 rounded-full border border-gray-100 shadow-sm"
            onClick={handleCloseModal}
            styleType="NONE"
          >
            <CloseIcon />
          </Button>
        </div>
        <div className="flex flex-col text-sm text-gray-700">
          <span>Plan: {sharePlan?.name}</span>
          <span>
            <AppFormattedMessage id="PLAN_TYPE" />: {sharePlan?.pool?.shareClass?.name}
          </span>
          <span>
            Vesting Weight: {Math.round((milestone?.weight || 0) * 100)}% ( cumulative{' '}
            {(100 * vestingCompletedWeight).toFixed(0)}
            %)
          </span>
          <span>
            <AppFormattedMessage id="MILESTONE_NAME" />: {milestone?.name}
          </span>
          <span>Milestone Date: {format(milestone?.targetDate, 'dd/MM/yyyy')}</span>
        </div>
        <div className="max-h-[400px] overflow-y-auto bg-white">
          <Table
            columnOrder={approveTargetBasedColumnMap}
            columnsTitle={{
              ...approveTargetBasedColumnTitle,
              [ApproveHurdleColumn.SELECT]: (
                <Field className="flex cursor-pointer items-center">
                  <Checkbox
                    checked={selectedVestingTasks.length > 0}
                    id="select-all-hurdle-plans"
                    onChange={(checked) =>
                      checked
                        ? onSelectAll(
                            pendingTargetBasedSharePlans?.map(
                              ({ id, targetBasedVestingMilestones, totalSharesCount }) => ({
                                vestingTaskId: id,
                                value: calcMilestoneIssuedShares({
                                  milestoneIndex: resolvedMilestoneIndex,
                                  milestones:
                                    targetBasedVestingMilestones ?? referenceMilestones ?? [],
                                  totalSharesCount,
                                }),
                                milestoneIndex: resolvedMilestoneIndex,
                              }),
                            ) || [],
                          )
                        : onDeselectAll()
                    }
                    selectIcon={<MinusIcon />}
                  />
                  <Label
                    className="absolute left-0 top-0 h-full w-full cursor-pointer"
                    htmlFor="select-all-hurdle-plans"
                  />
                </Field>
              ),
            }}
            tHeadClassNames={{ thClassName: 'pl-4' }}
          >
            {pendingTargetBasedSharePlans.map(
              ({ id, targetBasedVestingMilestones, ...grantItem }) => (
                <TargetBasedTableRow
                  id={id}
                  isSelected={selectedVestingTasks.some(
                    ({ vestingTaskId }) => vestingTaskId === id,
                  )}
                  key={id}
                  milestone={targetBasedVestingMilestones?.[resolvedMilestoneIndex] || milestone}
                  milestoneIndex={resolvedMilestoneIndex}
                  milestones={targetBasedVestingMilestones ?? referenceMilestones ?? []}
                  {...grantItem}
                  onRemoveSelect={(selectedId) =>
                    setSelectedVestingTasks((prev) =>
                      prev.filter(({ vestingTaskId }) => vestingTaskId !== selectedId),
                    )
                  }
                  onSelect={(item) =>
                    setSelectedVestingTasks((prev) => [
                      ...prev,
                      { ...item, milestoneIndex: resolvedMilestoneIndex },
                    ])
                  }
                />
              ),
            )}
          </Table>
        </div>
        <div className="flex h-11 w-full justify-between overflow-hidden text-sm">
          <Button className="w-fit font-[450] text-gray-700" styleType="NONE">
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="w-[260px] font-[550] text-white"
            data-permission-required
            disabled={selectedVestingTasks.length === 0}
            isLoading={isPending}
            onClick={() => {
              approve(selectedVestingTasks, {
                onSuccess: () => {
                  toast.success(<AppFormattedMessage id="EXERCISED_SUCCESSFULLY" />);
                  handleCloseModal();
                  invalidateQuery();
                  setSelectedVestingTasks([]);
                },
              });
            }}
          >
            Approve Target
          </Button>
        </div>
      </div>
    </AlertDialogWrapper>
  );
};
